import { get, patch, post, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

export enum UserStatus {
  UNCONFIRMED = 'UNCONFIRMED',
  ACTIVE = 'ACTIVE',
}

export interface TrialInfo {
  isActive: boolean;
  isExpired: boolean;
  isOverTokenLimit: boolean;
  trialEndsAt: string | null;
  daysRemaining: number;
  tokenLimit: number | null;
  tokensConsumed: number;
  percentUsed: number;
}

export interface OnboardingState {
  toursSeen: string[];
  dismissedTips: string[];
  completedAt?: string;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  legalHoursPerWeek?: number;
  organizationId: string;
  status: UserStatus;
  organization?: {
    id: string;
    name: string;
  };
  subscriptionEndDate?: string;
  subscriptionExtensionCount?: number;
  isSubscriptionActive?: boolean;
  canExtendSubscription?: boolean;
  daysUntilExpiration?: number;
  isExemptFromSubscription?: boolean;
  /** 'admin' manages the organization's members */
  role?: string;
  /** The organization's plan: trial, active, paused or cancelled */
  plan?: string;
  planEndsAt?: string | null;
  /** Why access is refused (e.g. TRIAL_EXPIRED, ORG_PAUSED); null when allowed */
  accessCode?: string | null;
  trial?: TrialInfo;
  onboarding?: OnboardingState | null;
}

export const getOwnUser = (accessToken?: string): Promise<ApiResponse<User>> => {
  const config = accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined;
  return get(
    `/user/self`,
    null,
    config
  );
};

export const findUser = (organizationId: string): Promise<ApiResponse<User[]>> => {
  return get(
    `/user`,
    {
      "organization-id": organizationId
    }
  );
};

export const deleteUser = (userId: string): Promise<ApiResponse<void>> => {
  return remove(`/user/${userId}`, null);
};

export const moveUser = (userId: string, organizationId: string): Promise<ApiResponse<void>> => {
  return patch(`/user/${userId}/organization`, { organizationId }, null);
};

export const extendUserSubscription = (userId: string): Promise<ApiResponse<{ success: boolean; newEndDate: string }>> => {
  return post(`/user/${userId}/extend-subscription`, {});
};

export const triggerUserPasswordReset = (userId: string): Promise<ApiResponse<{ success: boolean }>> => {
  return post(`/user/${userId}/reset-password`, {});
};

export const updateOwnName = (firstName: string, lastName: string): Promise<ApiResponse<User>> => {
  return patch(`/user/self`, { firstName, lastName }, null);
};

export const changeOwnPassword = (currentPassword: string, newPassword: string): Promise<ApiResponse<{ success: boolean }>> => {
  return post(`/user/self/password`, { currentPassword, newPassword });
};
